// Shared this-year vs last-year line chart for the Sales and Wholesale tiles.

import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatComp, formatDollars } from '@/lib/useRetailStores';
import {
  buildSeries,
  formatAxisK,
  formatPointLabel,
  type TrendPoint,
  type TrendPointRaw,
} from '@/lib/useHomeDashboard';
import { useChartPalette } from './chartColors';
import { MUTED_TEXT, deltaClass } from './tileText';

type Props = {
  points: TrendPointRaw[];
  grain: 'day' | 'hour';
  ariaLabel: string;
  /** Chart height in px. */
  height?: number;
  showLegend?: boolean;
  /** Drop the last-year line (no comparable data). */
  hideLy?: boolean;
};

type TipProps = {
  active?: boolean;
  payload?: Array<{ payload: TrendPoint }>;
  grain: 'day' | 'hour';
};

function TrendTooltip({ active, payload, grain }: TipProps) {
  const p = active ? payload?.[0]?.payload : undefined;
  if (!p) return null;
  return (
    <div className="rounded-md border border-amp-line bg-amp-white px-3 py-2 text-[12px] shadow-2 dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <div className="font-semibold text-amp-ink dark:text-amp-dark-ink">
        {formatPointLabel(p.t, grain)}
      </div>
      <div className="text-amp-ink dark:text-amp-dark-ink">This year: {formatDollars(p.ty)}</div>
      <div className={MUTED_TEXT}>Last year: {formatDollars(p.ly)}</div>
      <div className={deltaClass(p.pct)}>{formatComp(p.pct)}</div>
    </div>
  );
}

export function TrendChart({ points, grain, ariaLabel, height = 240, showLegend = true, hideLy = false }: Props) {
  const palette = useChartPalette();
  const series = buildSeries(points);

  return (
    <div className="min-w-0">
      {showLegend && (
        <div className={`mb-1 flex items-center gap-4 text-[12px] ${MUTED_TEXT}`}>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-0.5 w-5 rounded" style={{ backgroundColor: palette.accent }} /> This year
          </span>
          {!hideLy && (
            <span className="inline-flex items-center gap-1.5">
              <span className="w-5 border-t-2 border-dashed" style={{ borderColor: palette.muted }} /> Last year
            </span>
          )}
        </div>
      )}
      <div style={{ height }} role="img" aria-label={ariaLabel}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={series} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={palette.grid} />
            <XAxis
              dataKey="t"
              tickFormatter={(t: string) => formatPointLabel(t, grain)}
              tick={{ fill: palette.axis, fontSize: 12 }}
              stroke={palette.grid}
              minTickGap={24}
            />
            <YAxis
              tickFormatter={formatAxisK}
              tick={{ fill: palette.axis, fontSize: 12 }}
              stroke={palette.grid}
              width={48}
            />
            <Tooltip content={<TrendTooltip grain={grain} />} />
            {!hideLy && (
              <Line
                type="monotone"
                dataKey="ly"
                stroke={palette.muted}
                strokeWidth={2}
                strokeDasharray="5 4"
                dot={false}
                connectNulls
                isAnimationActive={false}
              />
            )}
            <Line
              type="monotone"
              dataKey="ty"
              stroke={palette.accent}
              strokeWidth={2.5}
              dot={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
